import { chatApi, ChatActorArgs, ChatConversationArgs } from "./chat";
import { MeetConnectionView, MeetOperationView } from "@/interfaces/GoogleMeet";
const meetUrl = (id: string) =>
  `/chat/conversations/${encodeURIComponent(id)}/meet`;
export const googleMeetApi = chatApi
  .enhanceEndpoints({ addTagTypes: ["MeetConnection", "MeetStatus"] })
  .injectEndpoints({
    endpoints: (build) => ({
      getMeetStatus: build.query<{ available: boolean }, ChatActorArgs>({
        query: (args) => ({ ...args, url: "/chat/meet-status" }),
        providesTags: ["MeetStatus"],
      }),
      getMeetConnection: build.query<MeetConnectionView, ChatActorArgs>({
        query: (args) => ({ ...args, url: "/admin/google-meet" }),
        providesTags: ["MeetConnection"],
      }),
      connectMeet: build.mutation<{ authorizationUrl: string }, ChatActorArgs>({
        query: (args) => ({
          ...args,
          url: "/admin/google-meet/connect",
          method: "POST",
        }),
      }),
      disconnectMeet: build.mutation<void, ChatActorArgs>({
        query: (args) => ({
          ...args,
          url: "/admin/google-meet",
          method: "DELETE",
        }),
        invalidatesTags: (_data, error) =>
          error ? [] : ["MeetConnection", "MeetStatus"],
      }),
      createChatMeet: build.mutation<
        MeetOperationView,
        ChatConversationArgs & { clientId: string }
      >({
        query: ({ clientId, ...args }) => ({
          ...args,
          url: meetUrl(args.conversationId),
          method: "POST",
          body: { clientId },
        }),
      }),
      getChatMeetOperation: build.query<
        MeetOperationView,
        ChatConversationArgs & { operationId: string }
      >({
        query: ({ operationId, ...args }) => ({
          ...args,
          url: `${meetUrl(args.conversationId)}/${encodeURIComponent(operationId)}`,
        }),
      }),
    }),
  });
export const {
  useGetMeetStatusQuery,
  useGetMeetConnectionQuery,
  useConnectMeetMutation,
  useDisconnectMeetMutation,
} = googleMeetApi;
export const isGoogleAuthorizationUrl = (value: string) => {
  try {
    const url = new URL(value);
    return (
      url.origin === "https://accounts.google.com" &&
      !url.username &&
      !url.password &&
      url.pathname === "/o/oauth2/v2/auth" &&
      !url.hash
    );
  } catch {
    return false;
  }
};
/** POST 409/503 can carry an operation, unlike an ordinary chat error. */
export const meetOperationFrom = (value: unknown): MeetOperationView | null => {
  if (!value || typeof value !== "object") return null;
  const operation = value as MeetOperationView;
  return typeof operation.id === "string" &&
    ["pending", "completed", "failed", "uncertain"].includes(
      operation.status
    ) &&
    (operation.code === null || typeof operation.code === "string")
    ? operation
    : null;
};
